import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../_helper/Auth";
import { IDLE_LOGOUT_MS } from "../tokenExpiry";
import { saveRedirectPath } from "../redirectStore";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
];

/** Minimum ms between actual timer resets (throttle). */
const THROTTLE_MS = 60 * 1000;

export function useIdleLogout() {
  const { token, removeToken, removeUser } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const timerRef = useRef(null);
  const lastResetRef = useRef(0);

  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  navigateRef.current = navigate;
  locationRef.current = location;

  const isAuthenticated = !!(
    token?.access_token &&
    token.access_token !== "null" &&
    token.access_token !== "undefined"
  );

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!isAuthenticated) {
      clearTimer();
      return;
    }

    const logout = () => {
      const loc = locationRef.current;
      saveRedirectPath(loc.pathname, loc.search, loc.hash);
      removeToken();
      removeUser();
      navigateRef.current("/login", {
        replace: true,
        state: { from: loc },
      });
    };

    const startTimer = () => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        console.warn("[auth] idle timeout reached — logging out.");
        logout();
      }, IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current >= THROTTLE_MS) {
        lastResetRef.current = now;
        startTimer();
      }
    };

    startTimer();
    lastResetRef.current = Date.now();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
    };
  }, [isAuthenticated, removeToken, removeUser]);
}
